"use client";

import { gsap } from "gsap";
import Image from "next/image";
import { useEffect, useRef, useState } from "react";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

const StoriesHero = () => {
  const [mount, setMount] = useState(false);

  useEffect(() => {
    setMount(true);
  }, []);
  const bars = [3, 5, 7, 10];
  const stats = [
    {
      icon: "/images/icons/users.webp",
      title: "Happy\nClients",
      value: "100+",
    },
    {
      icon: "/images/icons/Frame.webp",
      title: "Projects\nDelivered",
      value: "300+",
    },
    {
      icon: "/images/icons/globeIcon.webp",
      title: "Countries\nServed",
      value: "10+",
    },
    {
      icon: "/images/icons/trophyIcon.webp",
      title: "Retained & Referred Clients",
      value: "50+",
    },
  ];

  const gradient = "linear-gradient(110.23deg, #7949CD 6.47%, #6B58F5 44.08%, #0EA7F0 83.15%)";

  const root = useRef(null);
  const imageRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      const tl = gsap.timeline({
        defaults: { ease: "power3.out" },
      });

      tl.from(".hero-eyebrow", {
        y: 30,
        opacity: 0,
        duration: 0.8,
      })
        .from(
          ".hero-line",
          {
            y: 80,
            opacity: 0,
            duration: 1.1,
            stagger: 0.12,
          },
          "-=0.5",
        )
        .from(
          ".hero-sub",
          {
            y: 40,
            opacity: 0,
            duration: 1,
          },
          "-=0.6",
        );

      gsap.to(imageRef.current, {
        yPercent: 18,
        ease: "none",
        scrollTrigger: {
          trigger: root.current,
          start: "top top",
          end: "bottom top",
          scrub: 1,
        },
      });
    }, root);

    return () => ctx.revert();
  }, []);

  return (
    <section ref={root} className="relative hero-section w-full overflow-hidden bg-background-primary !px-4">
      {/* Mobile background (light) */}
      <div className="relative block lg:hidden w-full h-[260px] sm:h-[340px] dark:hidden">
        <Image src="/images/serviceBanners/successLatesW.webp" alt="Success Stories" fill priority className="object-cover object-right" />
      </div>
      {/* Mobile background (dark) */}
      <div className="relative hidden w-full h-[260px] sm:h-[340px] dark:block lg:dark:hidden">
        <Image src="/images/serviceBanners/successLates.webp" alt="Success Stories" fill priority className="object-cover object-right" />
      </div>

      {/* Desktop background (wrapper with ref={imageRef} for GSAP) */}
      <div ref={imageRef} className="absolute inset-0 w-full h-full top-0 hidden lg:block">
        <Image src="/images/serviceBanners/successLatesW.webp" alt="Success Stories" fill priority className="object-contain object-right dark:hidden" />
        <Image src="/images/serviceBanners/successLates.webp" alt="Success Stories" fill priority className="object-contain object-right hidden dark:block" />
      </div>

      {/* Light Overlay */}
      <div
        className="absolute inset-0 z-10 hidden lg:block dark:hidden"
        style={{
          background:
            "linear-gradient(90deg, rgba(240, 242, 255, 0.95) 0%, rgba(240, 242, 255, 0.85) 28%, rgba(240, 242, 255, 0.9) 35%, rgba(240, 242, 255, 0.25) 65%,  transparent 100%)",
        }}
      />
      {/* Dark Overlay */}
      <div
        className="absolute inset-0 z-10 hidden dark:lg:block"
        style={{
          background: "linear-gradient(90deg, #0d0f1e 0%, #0d0f1e 28%, rgba(13, 15, 30, 1) 15%, rgba(13, 15, 30, 0.3) 65%, transparent 100%)",
        }}
      />

      <div className="relative z-20 flex flex-col justify-center  px-6!  !py-10">
        <div className="max-w-2xl">
          <div className="flex items-center gap-4 mb-4! hero-eyebrow">
            <h3 className="text-xl sm:text-2xl font-medium uppercase text-transparent bg-clip-text bg-gradient-to-r from-cyan-600 to-purple-600 dark:from-cyan-400 dark:to-purple-600">
              OUR PORTFOLIO
            </h3>

            <span className="w-16 h-[2px] bg-gradient-to-r from-cyan-600 to-purple-600 dark:from-cyan-400 dark:to-purple-600 rounded-full"></span>
          </div>

          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-4xl xl:text-5xl 2xl:text-6xl font-bold leading-tight mb-6!">
            <span className="hero-line text-text-primary">Success</span>

            <span className="hero-line text-transparent bg-clip-text bg-gradient-to-r from-cyan-600 to-purple-600 dark:from-cyan-400 dark:to-purple-600"> Stories</span>
          </h1>

          <p className="hero-sub text-text-secondary text-sm sm:text-base lg:text-[16px] 2xl:text-lg md:max-w-[90vw] lg:max-w-[40vw] leading-relaxed !mb-12 w-full">
            We craft high performance digital solutions that help brands showcase their work, build strong credibility, and create lasting impressions across digital platforms.
            From visually engaging portfolio websites to scalable web applications, our focus is on delivering seamless user experiences, modern UI and UX design, and innovative
            technology solutions. These solutions are built to drive higher engagement, strengthen brand identity, improve customer trust, and support measurable business growth.
            We ensure every project is aligned with long term digital success, helping businesses stand out in a competitive market and achieve sustainable impact through
            technology driven excellence and continuous innovation.
          </p>
        </div>
        <div className="relative z-10 max-w-6xl mx-auto mb-8! lg:mb-16!">
          <div className="grid grid-cols-2 lg:grid-cols-8 md:grid-cols-4 gap-6 md:gap-8">
            {stats.map((item, index) => (
              <div key={index} className="flex flex-col items-center text-center group">
                <div className="relative flex items-center justify-center">
                  <div className="relative flex items-center justify-center border border-transparent bg-transparent backdrop-blur-sm group-hover:scale-105 transition-transform duration-300">
                    <img src={item.icon} alt="icons" className="h-14" />
                  </div>
                </div>

                <p
                  className="mt-5! text-2xl font-bold tracking-tighter"
                  style={{
                    background: gradient,
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                  }}
                >
                  {item.value}
                </p>
                <h3 className="mt-1! font-medium text-text-secondary lg:text-md text-sm font-medium leading-tight">{item.title}</h3>
              </div>
            ))}
          </div>
        </div>
        <div
          className="absolute hidden xl:block bottom-1/3 right-0 rounded-sm p-[2px]! group-hover:scale-105 transition-all duration-300!"
          style={{
            background: gradient,
          }}
        >
          <div
            className="relative w-[170px]  overflow-hidden! px-1! rounded-sm py-2!  flex! flex-col! items-center! text-center! backdrop-blur-xl!"
            style={{
              background: "var(--surface-primary)",
            }}
          >
            <div
              className="absolute inset-0!  pointer-events-none! rounded-sm"
              style={{
                border: "1px solid var(--border-primary)",
                boxShadow: "inset 0 0 25px rgba(0,0,0,0.04), 0 0 50px rgba(139,92,246,0.15)",
              }}
            />

            <div
              className="absolute inset-0! opacity-[0.05]!"
              style={{
                backgroundImage: "radial-gradient(circle, var(--text-primary) 1px, transparent 1px)",
                backgroundSize: "16px 16px",
              }}
            />

            <div className="relative z-10! flex! items-end! gap-[5px]! h-[58px]! mt-1!">
              {bars.map((h, i) => (
                <div
                  key={i}
                  className="rounded-t-[5px]!"
                  style={{
                    width: 10,
                    height: h * 5,
                    background: "linear-gradient(180deg, #38bdf8 0%, #2563eb 100%)",
                    boxShadow: "0 0 12px rgba(56,189,248,0.75)",
                    opacity: mount ? 1 : 0,
                    transform: mount ? "scaleY(1)" : "scaleY(0)",
                    transformOrigin: "bottom",
                    transition: `transform .7s ease ${0.5 + i * 0.08}s, opacity .4s ease ${0.5 + i * 0.08}s`,
                  }}
                />
              ))}

              <svg className="mb-1! ml-2! shrink-0!" width="28" height="28" viewBox="0 0 24 24" fill="none">
                <path
                  d="M5 17L10 11L14 15L20 6"
                  stroke="#38bdf8"
                  strokeWidth="2.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  style={{
                    filter: "drop-shadow(0 0 8px rgba(56,189,248,0.9))",
                  }}
                />

                <path d="M16 6H20V10" stroke="#c084fc" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>

            <div className="relative z-10! mt-6!">
              <p
                className="font-black! tracking-[0.24em]! leading-none!"
                style={{
                  fontSize: "13px",
                  color: "var(--text-primary)",
                }}
              >
                10+ YEARS.
              </p>

              <p
                className="font-black! tracking-[0.18em]! mt-2!"
                style={{
                  fontSize: "14px",
                  background: "linear-gradient(90deg, #38bdf8 0%, #d946ef 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                LIMITLESS GROWTH.
              </p>
            </div>

            <div className="relative z-10! mt-4!">
              <h2
                className="font-black! leading-none!"
                style={{
                  fontSize: "40px",
                  lineHeight: 0.85,
                  letterSpacing: "-0.06em",
                  background: "linear-gradient(135deg, #38bdf8 0%, #818cf8 45%, #d946ef 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  filter: "drop-shadow(0 0 18px rgba(129,140,248,0.55))",
                }}
              >
                10X
              </h2>

              <p
                className="mt-2!"
                style={{
                  fontSize: "14px",
                  color: "var(--text-secondary)",
                }}
              >
                Growth in a Decade
              </p>
            </div>

            <div
              className="relative z-10! mt-2! rounded-full! px-2! py-1! text-[10px]! font-medium! text-text-primary! overflow-hidden! transition-all! duration-300! hover:scale-[1.03]!"
              style={{
                border: "1px solid rgba(56,189,248,0.8)",
                background: "linear-gradient(90deg, rgba(14,165,233,0.16), rgba(168,85,247,0.18))",
                boxShadow: "0 0 16px rgba(56,189,248,0.3)",
              }}
            >
              And We’re Just Getting Started!
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        .hero-section {
          min-height: 80vh;
        }

        @media screen and (min-width: 1024px) and (max-width: 1440px) and (max-height: 850px) {
          .hero-section {
            min-height: 550px;
          }
        }

        @media screen and (min-width: 1024px) and (max-height: 768px) {
          .hero-section {
            min-height: 500px;
          }
        }

        @media screen and (min-width: 1441px) {
          .hero-section {
            min-height: 650px;
          }
        }
      `}</style>
    </section>
  );
};

export default StoriesHero;
